import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { useDemoMode } from '@/context/DemoModeContext'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useMutation, useQuery } from '@apollo/client'
import { SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import { Separator } from '@/components/ui/separator'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import {
  CREATE_ACCOUNT_MANAGER,
  GET_ACCOUNT_MANAGER,
  UPDATE_ACCOUNT_MANAGER,
} from '../accountManagerQueries'
import { CustomerRole } from '@/types/customer'
import { AccountManagerFormData } from '../types'
import useImpersonateB2BCustomer from '@/hooks/useImpersonateB2BCustomer'
import { features } from '@/config/features'
import PriceListSelect from '@/components/shared/inputComponents/PriceListSelect'
import ManagerSelect from '@/components/shared/inputComponents/ManagerSelect'
import PaletteSelect from '@/components/shared/inputComponents/PaletteSelect'
import CustomerAccountTable from '@/containers/CustomerAccounts/CustomerAccountTable'
import { ImageUploadV2 } from '@/components/shared/inputComponents/ImageUploadV2'
import SpokenLanguagesSelect from '@/components/shared/inputComponents/SpokenLanguagesSelect'

const formSchema = z.object({
  name: z.string().nonempty('Name is required'),
  surname: z.string().optional(),
  email: z.string().optional(),
  default_phone_number: z.string().optional(),
  is_b2b_user: z.boolean().default(true),
  b2b_company_name: z.string().optional(),
  account_code: z.string().min(1, 'Account Code is required'),
  b2b_customer_discount: z.number().optional(),
  managed_customers: z.array(z.number()),
  managers: z.array(z.number()).optional(),
  fabric_palettes: z.array(z.number()).optional(),
  price_listId: z.number().nullable().optional(),
  imageId: z.number().nullable().optional(),
  spoken_languages: z.array(z.string()).optional(),
  role: z
    .enum([
      CustomerRole.AGENT,
      CustomerRole.ACCOUNT_MANAGER,
      CustomerRole.ADMIN,
    ])
    .optional(),

  country: z.string().optional(),
  city: z.string().optional(),
  address_1: z.string().optional(),
  address_2: z.string().optional(),
  postal_code: z.string().optional(),
  state_region: z.string().optional(),
  is_configurator_enabled: z.boolean().default(false),
  is_claims_enabled: z.boolean().default(false),
})

interface AccountManagerFormProps {
  data?: {
    id: number
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function AccountManagerForm({
  data,
  onDirtyChange,
  onRequestClose,
}: AccountManagerFormProps) {
  const { isDemoMode } = useDemoMode()
  const [id, setId] = useState(data?.id)

  const {
    data: { findUniqueCustomer: accountManagerData } = {},
    loading: loadingData,
  } = useQuery(GET_ACCOUNT_MANAGER, {
    variables: {
      id,
    },
    skip: !id,
    fetchPolicy: 'network-only',
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      default_phone_number: '',
      is_b2b_user: true,
      b2b_company_name: '',
      account_code: '',
      managed_customers: [],
      managers: [],
      fabric_palettes: [],
      price_listId: null,
      imageId: null,
      spoken_languages: [],
      role: CustomerRole.ACCOUNT_MANAGER,

      country: '',
      city: '',
      address_1: '',
      address_2: '',
      postal_code: '',
      state_region: '',
      is_configurator_enabled: false,
      is_claims_enabled: false,
    },
  })

  const { control, handleSubmit, reset } = form

  const [updateAccountManager] = useMutation(UPDATE_ACCOUNT_MANAGER, {
    onError: e => {
      toast.error('Failed to update account manager')
    },
    onCompleted: () => {
      toast.success('Account manager updated successfully')
    },
    refetchQueries: ['GET_ACCOUNT_MANAGERS'],
  })

  const [createAccountManager] = useMutation(CREATE_ACCOUNT_MANAGER, {
    onError: e => {
      toast.error('Failed to create account manager')
    },
    onCompleted: response => {
      const newId = response.createB2Bcustomer.id
      setId(newId)
      toast.success('Account manager created successfully')
    },
    refetchQueries: ['GET_ACCOUNT_MANAGERS'],
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    if (id) {
      // `managed_customers` is not editable on this form (it's populated from
      // the record and rendered read-only via CustomerAccountTable above).
      // Re-sending it here would make the backend rewrite that relation on
      // every save, which can silently undo an edit to `managers` (e.g. when
      // a customer is their own manager, the delete-then-recreate of
      // managed_customers re-adds the self-referential row that the
      // managers edit just removed). Omit it entirely so the mutation
      // variable is absent (not null) and the backend leaves it untouched.
      const { managed_customers, ...rest } = values
      updateAccountManager({
        variables: {
          where: { id },
          ...rest,
          phone_number: values.default_phone_number, // Map default_phone_number to phone_number
        },
      })
    } else {
      createAccountManager({
        variables: {
          ...values,
          phone_number: values.default_phone_number, // Map default_phone_number to phone_number
        },
      })
    }
  }

  useEffect(() => {
    if (accountManagerData) {
      console.log('accountManagerData', accountManagerData)
      reset({
        name: accountManagerData.name,
        surname: accountManagerData.surname || '',
        email: accountManagerData.email?.trim() || '',
        default_phone_number: accountManagerData.default_phone_number || '',
        is_b2b_user: true,
        b2b_company_name: accountManagerData.b2b_company_name || '',
        account_code: accountManagerData.account_code || '',
        managed_customers:
          accountManagerData.managed_customers?.map(
            (mc: any) => mc.customer.id
          ) || [],
        managers:
          accountManagerData.managers?.map((m: any) => m.managerId) || [],
        fabric_palettes:
          accountManagerData.fabric_palettes?.map((p: any) => p.id) || [],
        price_listId: accountManagerData.price_listId || null,
        imageId: accountManagerData.imageId || null,
        spoken_languages: accountManagerData.spoken_languages || [],
        role: accountManagerData.role || undefined,

        country: accountManagerData.country || '',
        city: accountManagerData.city || '',
        address_1: accountManagerData.address_1 || '',
        address_2: accountManagerData.address_2 || '',
        postal_code: accountManagerData.postal_code || '',
        state_region: accountManagerData.state_region || '',
        is_configurator_enabled:
          accountManagerData.is_configurator_enabled || false,
        is_claims_enabled: accountManagerData.is_claims_enabled || false,
      })
    }
  }, [accountManagerData, reset])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  const {
    impersonate,
    loading: impersonateLoading,
    error: impersonateError,
    data: impersonateData,
  } = useImpersonateB2BCustomer()

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Account Manager</SheetTitle>
          {id && features.impersonation.storefront && (
            <Button
              type="button"
              onClick={() => {
                // Add your login logic here
                impersonate(id)
              }}
            >
              Impersonate
            </Button>
          )}
        </div>
        {/* <SheetDescription></SheetDescription> */}
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Account Code"
                control={control}
                name="account_code"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder="Account Code"
                        type="text"
                        {...field}
                        className={isDemoMode ? 'blur-sm select-none' : ''}
                        readOnly={isDemoMode}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <InputFieldContainer
                label="Full Name"
                control={control}
                name="name"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder="Name Surname"
                        type="text"
                        {...field}
                        className={isDemoMode ? 'blur-sm select-none' : ''}
                        readOnly={isDemoMode}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <InputFieldContainer
                label="Email"
                control={control}
                name="email"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder="email@company.com"
                        type="email"
                        {...field}
                        className={isDemoMode ? 'blur-sm select-none' : ''}
                        readOnly={isDemoMode}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <InputFieldContainer
                label="Managers"
                control={control}
                name="managers"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <div className={isDemoMode ? 'blur-sm select-none pointer-events-none' : ''}>
                        <ManagerSelect field={field} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <InputFieldContainer
                label="Role"
                control={control}
                name="role"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={CustomerRole.AGENT}>
                            Agent
                          </SelectItem>
                          <SelectItem value={CustomerRole.ACCOUNT_MANAGER}>
                            Manager
                          </SelectItem>
                          <SelectItem value={CustomerRole.ADMIN}>
                            Admin
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <InputFieldContainer
                label="Profile Image"
                control={control}
                name="imageId"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <ImageUploadV2
                        field={field}
                        imageName="account-manager-profile"
                        existingImageUrl={accountManagerData?.image?.src}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Separator />
              {id && <CustomerAccountTable customerId={id} />}
              <InputFieldContainer
                label="Company Name"
                control={control}
                name="b2b_company_name"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder="Company Name"
                        type="text"
                        {...field}
                        className={isDemoMode ? 'blur-sm select-none' : ''}
                        readOnly={isDemoMode}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <InputFieldContainer
                label="Phone Number"
                control={control}
                name="default_phone_number"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder="Phone Number"
                        type="tel"
                        {...field}
                        className={isDemoMode ? 'blur-sm select-none' : ''}
                        readOnly={isDemoMode}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <InputFieldContainer
                label="Spoken Languages"
                description="Select languages the account manager can communicate in"
                control={control}
                name="spoken_languages"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <SpokenLanguagesSelect
                        value={field.value || []}
                        onChange={field.onChange}
                        placeholder="Select spoken languages..."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Separator />
              <InputFieldContainer
                label="Price List"
                control={control}
                name="price_listId"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <PriceListSelect field={field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <InputFieldContainer
                label="Fabric Palettes"
                description="Limit fabric palettes for this account manager."
                control={control}
                name="fabric_palettes"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <PaletteSelect field={field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <InputFieldContainer
                label="Configurator"
                control={control}
                name="is_configurator_enabled"
                render={({ field }) => (
                  <FormItem className="col-span-3 flex items-center gap-2 min-h-10">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                    <div className="text-xs text-gray-500 !mt-0">
                      Enable configurator access for this account manager.
                    </div>
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Claims"
                control={control}
                name="is_claims_enabled"
                render={({ field }) => (
                  <FormItem className="col-span-3 flex items-center gap-2 min-h-10">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                    <div className="text-xs text-gray-500 !mt-0">
                      Enable claims portal for this account manager
                    </div>
                  </FormItem>
                )}
              />

              <Separator />

              <Button type="submit" disabled={loadingData}>
                {id ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
